import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Button,
  Space,
  message,
  Alert,
  Statistic,
  Row,
  Col,
  Modal
} from 'antd';
import {
  FileTextOutlined,
  SaveOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import Editor from '../other/Editor';
import {
  getDocumentationPrivacy,
  updateDocumentation,
  clearMessages,
  updateDocumentationDraft,
  resetDocumentationDraft
} from '../../features/account/documentationPrivacySlice';

const DocumentationManager = () => {
  const dispatch = useDispatch();

  const {
    documentation,
    documentationDraft,
    hasUnsavedDocumentation,
    loading,
    documentationLoading,
    error,
    success,
    lastUpdated
  } = useSelector((state) => state.documentationPrivacy);

  useEffect(() => {
    dispatch(getDocumentationPrivacy());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      message.success(success);
      dispatch(clearMessages());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [error, dispatch]);

  // Handle documentation content change
  const handleDocumentationChange = (content) => {
    dispatch(updateDocumentationDraft(content));
  };

  // Save documentation
  const handleSaveDocumentation = () => {
    dispatch(updateDocumentation(documentationDraft));
  };

  // Reset documentation to saved version
  const handleResetDocumentation = () => {
    Modal.confirm({
      title: 'Reset Documentation',
      content: 'Are you sure you want to discard your changes and reset to the last saved version?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Reset',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        dispatch(resetDocumentationDraft());
      }
    });
  };

  // Refresh content
  const handleRefresh = () => {
    if (hasUnsavedDocumentation) {
      Modal.confirm({
        title: 'Refresh Content',
        content: 'You have unsaved changes. Refreshing will discard these changes. Continue?',
        icon: <WarningOutlined />,
        okText: 'Refresh',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
          dispatch(getDocumentationPrivacy());
        }
      });
    } else {
      dispatch(getDocumentationPrivacy());
    }
  };

  // Get word count
  const getWordCount = (text) => {
    return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
  };

  // Get character count
  const getCharCount = (text) => {
    return text.length;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-green-600 rounded-full flex items-center justify-center">
              <FileTextOutlined className="text-xl text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-1">Documentation Manager</h3>
              <p className="text-gray-600">Create and edit your application documentation</p>
            </div>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
            {hasUnsavedDocumentation && (
              <>
                <Button onClick={handleResetDocumentation} disabled={loading}>
                  Reset
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveDocumentation}
                  loading={documentationLoading}
                >
                  Save Documentation
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* Statistics */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8}>
            <Statistic
              title="Word Count"
              value={getWordCount(documentationDraft)}
              prefix={<FileTextOutlined />}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Characters"
              value={getCharCount(documentationDraft)}
              suffix={`/ 50,000`}
              valueStyle={{ 
                color: getCharCount(documentationDraft) > 45000 ? '#f5222d' : '#52c41a' 
              }}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Status"
              value={hasUnsavedDocumentation ? "Unsaved" : "Saved"}
              valueStyle={{ 
                color: hasUnsavedDocumentation ? '#fa8c16' : '#52c41a' 
              }}
            />
          </Col>
        </Row>

        {lastUpdated && (
          <div className="mt-4 text-sm text-gray-500">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </div>
        )}
      </Card>

      {/* Unsaved Changes Alert */}
      {hasUnsavedDocumentation && (
        <Alert
          message="Unsaved Changes"
          description="You have unsaved changes in your documentation. Don't forget to save your work."
          type="warning"
          showIcon
          action={
            <Button size="small" type="primary" onClick={handleSaveDocumentation}>
              Save Changes
            </Button>
          }
        />
      )}

      {/* Editor */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-lg font-medium">Documentation Content</h4>
              <p className="text-gray-600">
                Write comprehensive documentation for your application
              </p>
            </div>
          </div>
          
          <div className="editor-container border border-border-light rounded-lg" style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
            <Editor
              value={documentationDraft}
              onChange={handleDocumentationChange}
              placeholder="Start writing your documentation..."
              minHeight="600px"
              stickyToolbar={true}
              style={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              editorStyle={{
                height: 'calc(100% - 60px)', // Subtract toolbar height
                overflow: 'auto',
                border: 'none'
              }}
              toolbarStyle={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'white',
                borderBottom: '1px solid #e8e8e8',
                borderRadius: '8px 8px 0 0'
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DocumentationManager;